<ng-container *transloco="let t">
  <div>
    <!-- Fields card -->
    <div class="bg-surface-card border border-stroke-soft rounded-[12px] p-5 sm:p-6 mb-4">
      <!-- Row 1: Order Title / Order Language / Remind Recipient Every -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <!-- Order Title -->
        <div class="flex flex-col gap-2">
          <juno-libs-ui-input [formField]="form().name" label="CONTRACT_ORDER.ORDER_TITLE" />
        </div>

        <!-- Order Language -->
        <div class="flex flex-col gap-2">
          <juno-libs-ui-select
            [formField]="form().language"
            [options]="languageOptions"
            label="CONTRACT_ORDER.ORDER_LANGUAGE"
          />
        </div>

        <!-- Remind Recipient Every -->
        <div class="flex flex-col gap-2">
          <juno-libs-ui-select
            [formField]="form().reminderInterval"
            [options]="reminderIntervalOptions()"
            label="CONTRACT_ORDER.REMIND_PERIOD"
          />
        </div>
      </div>

      <!-- Row 2: Expiration Period / Signing Method -->
      <div class="grid grid-cols-1 sm-md:grid-cols-2 gap-4 mb-4">
        <!-- Expiration Period -->
        <div class="flex flex-col gap-2">
          <juno-libs-ui-select
            [formField]="form().expirationPeriod"
            [options]="expirationPeriodOptions()"
            label="CONTRACT_ORDER.EXPIRATION_PERIOD"
          />
        </div>

        <!-- Signing Method -->
        <div class="flex flex-col gap-2">
          <juno-libs-ui-select
            [formField]="form().signingMethod"
            [options]="signingMethodOptions()"
            [placeholder]="t('CONTRACT_ORDER.NOT_SPECIFIED')"
            label="CONTRACT_ORDER.SIGNING_METHOD"
          />
        </div>
      </div>

      <!-- Verification Method (conditional on Doodle) -->
      @if (form().signingMethod().value() === doodleSigningMethod) {
      <div class="flex flex-col gap-2 mb-4">
        <juno-libs-ui-select
          [formField]="form().verificationMethod"
          [options]="verificationMethodOptions"
          label="CONTRACT_ORDER.VERIFICATION_METHOD"
        />
      </div>
      }

      <!-- Login Required checkbox -->
      <juno-libs-ui-checkbox
        [formField]="form().requireSignerAuthentication"
        label="CONTRACT_ORDER.LOGIN_TO_VIEW_DOCUMENTS"
        [appliedClasses]="{ box: 'size-4.5!', label: 'text-sm!' }"
      />
    </div>

    <!-- Receive a Copy (always expanded, no section toggle) -->
    <ng-template #addReceiverButton let-fullWidth="fullWidth">
      <dss-libs-ui-button
        type="button"
        variant="outline"
        [label]="'CONTRACT_ORDER.ADD_RECEIVER'"
        [iconMetaData]="addReceiverIcon"
        [class]="fullWidth ? 'flex! w-full' : ''"
        (clickOutput)="addRecipient()"
      ></dss-libs-ui-button>
    </ng-template>

    <div class="rounded-lg overflow-visible">
      <!-- Section header -->
      <div class="flex flex-col gap-3 px-5 py-4 rounded-t-lg bg-surface-subtle border border-b-0 border-stroke-soft">
        <!-- Row 1: title + count | Add Receiver (desktop only) -->
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <span class="text-base sm-md:text-[1.125rem] font-medium text-ink-secondary whitespace-nowrap">
              {{ t('CONTRACT_ORDER.RECEIVE_COPY') }}
            </span>
            <span class="text-xs font-normal text-ink-muted whitespace-nowrap">
              {{ form().notificationRecipients.length | translocoDecimal }} {{ t('CONTRACT_ORDER.RECEIVERS_LABEL') }}
            </span>
          </div>

          <div class="hidden md:flex">
            <ng-container
              [ngTemplateOutlet]="addReceiverButton"
              [ngTemplateOutletContext]="{ fullWidth: false }"
            ></ng-container>
          </div>
        </div>

        <!-- Row 2: Add Receiver full-width (mobile only) -->
        <div class="md:hidden">
          <ng-container
            [ngTemplateOutlet]="addReceiverButton"
            [ngTemplateOutletContext]="{ fullWidth: true }"
          ></ng-container>
        </div>
      </div>

      <!-- Section body (always visible) -->
      <div class="bg-surface-card border border-t-0 border-stroke-soft rounded-b-[8px] px-5 py-6 flex flex-col gap-4">
        @if (form().notificationRecipients.length === 0) {
        <!-- Empty state -->
        <div class="flex flex-col items-center justify-center h-[180px] gap-3">
          <mat-icon class="text-ink-subtle! size-12!" svgIcon="user-circle-outline"></mat-icon>
          <p class="text-sm font-semibold text-ink-muted text-center">{{ t('CONTRACT_ORDER.NO_RECEIVERS_YET') }}</p>
        </div>
        } @else {
        <!-- Receiver cards -->
        @for (receiver of form().notificationRecipients; track receiver; let receiverIndex = $index) {
        <div class="bg-white rounded-[12px] p-4">
          <!-- Card header -->
          <div class="flex items-center justify-between">
            <div
              class="flex items-center gap-2 cursor-pointer select-none flex-1"
              (click)="toggleReceiver(receiver)"
              (keydown.enter)="toggleReceiver(receiver)"
              tabindex="0"
            >
              <svg
                class="size-4 text-ink-secondary transition-transform duration-200 shrink-0"
                [class.rotate-90]="expandedReceivers().has(receiver)"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clip-rule="evenodd"
                />
              </svg>
              <div class="flex items-center gap-2">
                <div class="size-7 rounded-full bg-primary-tint flex items-center justify-center shrink-0">
                  <span class="text-xs font-medium text-primary leading-none">
                    {{ receiverIndex + 1 | translocoDecimal : { minimumIntegerDigits: 2 } }}
                  </span>
                </div>
                <span class="text-base font-semibold text-ink-secondary">
                  {{ t('CONTRACT_ORDER.RECEIVER_DETAILS') }}
                </span>
              </div>
            </div>
            <!-- Trash button -->
            <button
              mat-icon-button
              type="button"
              (click)="deleteRecipient(receiver)"
              class="text-danger! hover:text-danger-hover transition-colors shrink-0 p-1"
            >
              <mat-icon svgIcon="trash-bin-minimalistic-outline"></mat-icon>
            </button>
          </div>

          <!-- Card body (expanded) -->
          @if (expandedReceivers().has(receiver)) {
          <div class="mt-4 pt-4 border-t border-stroke-soft">
            <div class="grid grid-cols-1 sm-md:grid-cols-2 gap-4">
              <!-- Name -->
              <div class="flex flex-col gap-2">
                <juno-libs-ui-input [formField]="receiver.name" label="CONTRACT_ORDER.NAME" />
              </div>

              <!-- Email -->
              <div class="flex flex-col gap-2">
                <juno-libs-ui-input [formField]="receiver.email" label="CONTRACT_ORDER.EMAIL" type="email" />
              </div>
            </div>
          </div>
          }
        </div>
        } }
      </div>
    </div>
  </div>
</ng-container>
